import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {MatChipsModule} from '@angular/material/chips';
import {MatDividerModule} from '@angular/material/divider';
import {MatIconModule} from '@angular/material/icon';
import {FlexModule} from '@angular/flex-layout';
import {Subscription} from 'rxjs';
import {CrudTableComponent} from '@ui-kit/webkit/components/crud-table/crud-table.component';
import {TableActionItem} from '@ui-kit/webkit/components/crud-table/crud-table.model';
import {LoadingService} from '@ui-kit/framework/util/service/loading.service';
import {ApplicationAssessmentStatus, Retailer, TenantType} from '@features/leads/retailers/retailers.model';
import {CrudRetailerContacts} from '@features/leads/retailers/retailers-list/retailer-contacts.model';
import {RetailerCacheService} from '../retailer-cache.service';
import {RetailerCreateFormComponent} from '../retailer-create-form/retailer-create-form.component';
import {RetailerLookupComponentOptions} from "../retailer-lookup.model";

export type RetailerLookupDialogData = RetailerLookupComponentOptions & { offeringUuid: string; }

@Component({
  selector: 'app-retailer-lookup-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatChipsModule,
    MatDividerModule,
    MatIconModule,
    FlexModule,
    CrudTableComponent,
    RetailerCreateFormComponent,
  ],
  templateUrl: './retailer-lookup-dialog.component.html',
  styleUrls: ['./retailer-lookup-dialog.component.scss'],
})
export class RetailerLookupDialogComponent implements OnInit, OnDestroy {
  protected readonly dialogData = inject<RetailerLookupDialogData>(MAT_DIALOG_DATA);
  protected readonly dialogRef = inject(MatDialogRef<RetailerLookupDialogComponent, Retailer | null>);
  protected readonly cacheService = inject(RetailerCacheService);
  protected readonly loader = inject(LoadingService);

  protected readonly offeringUuid = computed(() => this.dialogData.offeringUuid);
  protected readonly allowedRetailerTenantTypes = computed<TenantType[]>(() => this.dialogData.allowedRetailerTenantTypes ?? Object.values(TenantType));
  protected readonly allowedCurationStatus = computed<ApplicationAssessmentStatus[]>(() => this.dialogData.allowedCurationStatus ?? Object.values(ApplicationAssessmentStatus));
  protected readonly allowedRetailerFilterPredicate = computed<(filter: Retailer) => boolean>(() => this.dialogData.allowedRetailerFilterPredicate ?? ((f: Retailer) => true));

  protected readonly retailers = this.cacheService.getRetailersSignal(this.dialogData.offeringUuid);

  protected readonly tableData = computed(() =>
    this.retailers()
      .filter(r => this.allowedRetailerTenantTypes().includes(r.tenantType))
      .filter(r => this.allowedCurationStatus().includes(r.curated))
      .filter(r => this.allowedRetailerFilterPredicate)
      .map(r => new CrudRetailerContacts(r))
  );

  protected readonly selectedRetailer = signal<Retailer | null>(null);
  protected readonly showCreateForm = signal(false);

  protected readonly CrudRetailerContacts = CrudRetailerContacts;
  protected readonly showColumns = ['id', 'companyName', 'retailerName', 'ownerEmail', 'phoneNumber', 'curated', 'tenantType'];

  private readonly createAction = new EventEmitter<void>();
  private createActionSub!: Subscription;

  protected readonly tableActions: TableActionItem[] = [
    {
      icon: 'add',
      label: 'Create Retailer',
      actionDataSource: 'None',
      enabled: 'Always',
      keepSelectedRowOnClick: false,
      eventEmitter: this.createAction,
      includeData: false,
    },
  ];

  ngOnInit(): void {
    this.cacheService.initialize(this.offeringUuid());
    this.createActionSub = this.createAction.subscribe(() => this.showCreateForm.set(true));
  }

  ngOnDestroy(): void {
    this.createActionSub?.unsubscribe();
  }

  protected onRowSelected(row: CrudRetailerContacts | null): void {
    if (row?.id != null) {
      const retailer = this.retailers().find(r => r.id === row.id) ?? null;
      this.selectedRetailer.set(retailer);
    } else {
      this.selectedRetailer.set(null);
    }
  }

  protected onReloadData(): void {
    this.cacheService.refresh(this.dialogData.offeringUuid);
  }

  protected onRetailerCreated(retailer: Retailer): void {
    this.cacheService.addRetailer(this.dialogData.offeringUuid, retailer);
    this.selectedRetailer.set(retailer);
    this.showCreateForm.set(false);
  }

  protected backToList(): void {
    this.showCreateForm.set(false);
  }

  protected cancel(): void {
    this.dialogRef.close(null);
  }

  protected confirmSelection(): void {
    this.dialogRef.close(this.selectedRetailer());
  }
}
